import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  TrendingUp,
  Wallet,
  CalendarRange,
} from "lucide-react";

const Stat = ({ label, value, icon: Icon, to, testid }) => {
  const Body = (
    <div
      className="bg-white border border-[#e5e0d8] rounded-sm p-6 hover:border-[#234333]/40 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
          {label}
        </div>
        <Icon size={18} strokeWidth={1.5} className="text-[#234333]" />
      </div>
      <div className="mt-4 font-serif text-4xl text-[#1c1a17]">{value}</div>
    </div>
  );
  return to ? <Link to={to}>{Body}</Link> : Body;
};

export default function InstituteDashboard() {
  const [info, setInfo] = useState(null);
  const [counts, setCounts] = useState({ depts: "—", intake: "—", fees: "—" });
  const [currentFy, setCurrentFy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [i, d, n, f] = await Promise.all([
          api.get("/institute/info"),
          api.get("/institute/departments"),
          api.get("/institute/intake"),
          api.get("/institute/fees"),
        ]);
        setInfo(i.data);
        const cur = (i.data.financial_years || []).find((x) => x.is_current);
        setCurrentFy(cur || null);
        const fyId = cur?.id;
        setCounts({
          depts: d.data.length,
          intake: fyId ? n.data.filter((r) => r.financial_year_id === fyId).length : n.data.length,
          fees: fyId ? f.data.filter((r) => r.financial_year_id === fyId).length : f.data.length,
        });
      } catch (err) {
        // Empty state will render; log for diagnostics.
        console.error("InstituteDashboard load failed", err);
      }
    })();
  }, []);

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="institute-dashboard">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
          Institute Portal
        </div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">
          {info?.institute?.name || "Institute"}
        </h1>
        <p className="mt-3 text-sm text-[#6b655b] max-w-2xl">
          {info?.institute?.type ? `${info.institute.type} · ` : ""}
          {info?.institute?.code ? `Code: ${info.institute.code}` : null}
          {currentFy ? ` · Current FY ${currentFy.label}` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <Stat
          label="Departments"
          value={counts.depts}
          icon={GraduationCap}
          to="/institute/departments"
          testid="i-stat-depts"
        />
        <Stat
          label={`Intake records · ${currentFy?.label || "—"}`}
          value={counts.intake}
          icon={TrendingUp}
          to="/institute/intake"
          testid="i-stat-intake"
        />
        <Stat
          label={`Fees records · ${currentFy?.label || "—"}`}
          value={counts.fees}
          icon={Wallet}
          to="/institute/fees"
          testid="i-stat-fees"
        />
      </div>

      <div className="bg-white border border-[#e5e0d8] rounded-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <CalendarRange size={16} strokeWidth={1.5} className="text-[#234333]" />
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
            How this works
          </div>
        </div>
        <p className="text-sm text-[#6b655b]">
          1. Add your <strong>Departments</strong> first (HoDs, course type, approval body).{" "}
          2. For each financial year, record <strong>Sanctioned Intake</strong> and{" "}
          <strong>Approved Fees</strong> per department, attaching the official approval letter
          (PDF, up to 10 MB). Past financial year records are read-only — only Super Admins can
          edit them.
        </p>
      </div>
    </div>
  );
}
